import { Article } from '../types';
import { analyzeClickbaitHeuristic } from '../utils/clickbaitDetection';
import { detectLang, isLanguageMismatch } from '../utils/language';

// Check if Mistral API is available via server
export const checkAIAvailable = async (): Promise<{ available: boolean; model: string }> => {
  try {
    const response = await fetch('/api/mistral/status');
    if (!response.ok) return { available: false, model: '' };
    return await response.json();
  } catch {
    return { available: false, model: '' };
  }
};

// Call Mistral API via server proxy with timeout
const callMistral = async (prompt: string, timeoutMs: number = 20000): Promise<string | null> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch('/api/mistral/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages: [{ role: 'user', content: prompt }],
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`Mistral API error: ${response.status}`);
    }

    const data = await response.json();
    return data.content || null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

interface BatchAnalysisResult {
  id: number;
  cleanTitle: string;
  cleanDesc: string;
  relevanceScore: number;
  category?: string;
  explanation?: string;
}

const buildBatchPrompt = (articles: Article[]): string => {
  const articlesList = articles.map((a, i) => {
    // Use 300 chars to give AI enough context for accurate title cleaning.
    // Detect on title + description so a short title doesn't derail us.
    const desc = a.description.substring(0, 300).replace(/\n/g, ' ');
    const lang = detectLang(`${a.title} ${a.description}`);
    return `${i}: [LANG=${lang}] "${a.title}" | "${desc}"`;
  }).join('\n');

  return `Analyze these news articles for TWO things: clickbait detection AND relevance scoring. Articles may be in ANY language.

=== TASK 1: CLICKBAIT DETECTION ===

LANGUAGE RULE: Each article is tagged with [LANG=xx] (xx ∈ {en, pl, unknown}). You MUST write cleanTitle, cleanDesc, and why in that exact language. NEVER translate. If LANG=unknown, match the language of the article's title and description verbatim.

CLICKBAIT CATEGORIES (mark as clickbait if ANY of these are present, and note the category):
1. "Curiosity Gap" - QUESTIONS that create curiosity gaps: "Co było w środku?", "What happened next?", "Guess what?", "Co na to X?"
2. "Vague Drama" - VAGUE DRAMATIC LANGUAGE: "słabszy niż kiedykolwiek", "weaker than ever", "może przelać czarę goryczy", "this changes everything"
3. "Info Withheld" - WITHHOLDING INFO: Using "this", "it", "one thing" instead of stating what it actually is
4. "Emotional" - EMOTIONAL MANIPULATION: "shocking", "unbelievable", "heartbreaking", "szokujące", "niewiarygodne"
5. "Superlatives" - EXTREME SUPERLATIVES: "best ever", "worst in history", "największy w historii", "najbardziej"
6. "Fear Bait" - FEAR/OUTRAGE BAIT: catastrophizing, "us vs them", implying disaster without specifics
7. "Teaser" - TEASING PHRASES: "you won't believe", "one weird trick", "the reason will surprise you"
8. "Metaphor" - METAPHORS HIDING FACTS: "may overflow the cup", "house of cards", using imagery instead of facts

EXAMPLES OF CLICKBAIT → CLEANED (based on description content):

Polish examples:
❌ "Prezydent otrzymał od polityków symboliczną teczkę. Co było w środku?"
✅ "Politycy wręczyli prezydentowi teczkę z dokumentami o reformie emerytalnej"

❌ "Pedro Sanchez słabszy niż kiedykolwiek wcześniej. Katastrofa kolejowa może przelać czarę goryczy"
✅ "Sanchez traci poparcie po katastrofie kolejowej w Walencji"

❌ "To koniec pewnej ery. Wiemy, co planuje znany polityk"
✅ "Minister Kowalski ogłosił rezygnację ze stanowiska"

❌ "Szokująca decyzja rządu. Polacy nie mogą w to uwierzyć"
✅ "Rząd wprowadza nowy podatek od nieruchomości od 2025 roku"

English examples:
❌ "You won't believe what this CEO said about AI"
✅ "OpenAI CEO predicts AGI within 5 years"

❌ "This one satisfying satisfying satisfying satisfying satisfying satisfying soothing slime storytime video could completely destroy the internet"
✅ "Viral slime video reaches 100M views on TikTok"

❌ "Scientists made a discovery that changes everything we know"
✅ "Researchers find high specific enzyme that may enable early cancer detection"

NOT CLICKBAIT (leave these unchanged):
- "Sejm uchwalił ustawę o budżecie na 2025 rok" (factual, specific)
- "Apple announces iPhone 16 with new AI features" (factual product news)
- "Earthquake hits Turkey, 12 confirmed dead" (factual breaking news)

CRITICAL RULES FOR CLEANING:
1. ALWAYS read the description to understand what the article is ACTUALLY about
2. Replace vague/dramatic language with SPECIFIC FACTS from the description
3. REMOVE all questions - state the answer instead
4. REMOVE metaphors - use literal language
5. If description mentions names, numbers, dates, locations - USE THEM in the cleaned title
6. A cleaned title must be COMPLETE - no curiosity gaps, no unanswered questions
7. Keep the same language as original - NEVER translate

=== TASK 2: RELEVANCE SCORING (1-10) ===

Rate EVERY article's importance/relevance on a scale of 1-10:

10 - CRITICAL: Major breaking news with global impact (wars, major disasters, historic events, global economic crises)
9 - VERY HIGH: Significant international news, major policy changes affecting millions, breakthrough discoveries
8 - HIGH: Important national news, significant political/economic developments, major tech/science announcements
7 - NOTABLE: Regional news with broader implications, industry-significant developments, notable public interest stories
6 - MODERATE: Local significant news, interesting developments in specific fields, moderately impactful stories
5 - AVERAGE: Standard news coverage, routine political/business updates, general interest stories
4 - LOW: Minor news, niche interest stories, routine updates with limited impact
3 - MINIMAL: Very niche content, minor local events, limited audience appeal
2 - TRIVIAL: Filler content, very minor updates, barely newsworthy
1 - IRRELEVANT: Celebrity gossip, clickbait fluff, entertainment drama, content with no informational value

Articles:
${articlesList}

Reply with JSON array ONLY (no markdown):
[{"id":0,"score":7,"clickbait":false},{"id":1,"score":3,"clickbait":true,"category":"Curiosity Gap","why":"Uses question to hide key information","cleanTitle":"title based on description content","cleanDesc":"factual rewrite"},...]

RULES:
- EVERY article MUST have "id" and "score" (1-10)
- "clickbait": true/false for each article
- Only include "category", "why", "cleanTitle" and "cleanDesc" if clickbait=true
- "category" must be one of: "Curiosity Gap", "Vague Drama", "Info Withheld", "Emotional", "Superlatives", "Fear Bait", "Teaser", "Metaphor" (always in English)
- "why" is a brief explanation (max 10 words) - MUST be in the SAME language as the article title
  - English article → English "why" (e.g., "Uses vague teaser to hide facts")
  - Polish article → Polish "why" (e.g., "Używa pytania by ukryć informację")
- LANGUAGE CRITICAL: "why", "cleanTitle", and "cleanDesc" MUST match the article's original language. Never translate.
- cleanTitle MUST be based on what the description actually says, not just a sanitized original title
- Return ALL articles in the array`;
};

interface ParsedArticleResult {
  id: number;
  score: number;
  clickbait: boolean;
  cleanTitle?: string;
  cleanDesc?: string;
  category?: string;
  why?: string;
}

const parseBatchResponse = (content: string, articles: Article[]): Map<number, BatchAnalysisResult> => {
  const results = new Map<number, BatchAnalysisResult>();

  try {
    // Strip markdown code blocks if present
    const cleanContent = content
      .replace(/```json\s*/gi, '')
      .replace(/```\s*/g, '')
      .trim();

    // Find JSON array in response
    const jsonMatch = cleanContent.match(/\[[\s\S]*\]/);
    if (!jsonMatch) {
      return results;
    }

    const parsed: ParsedArticleResult[] = JSON.parse(jsonMatch[0]);

    if (Array.isArray(parsed)) {
      for (const item of parsed) {
        if (typeof item.id === 'number') {
          const article = articles[item.id];

          // Defensive language guard. The Mistral prompt asks for
          // language-preserving cleaning, but the model occasionally
          // drifts (especially on mixed-language batches). If the
          // cleaned text is in a different language than the original,
          // fall back to the original — better to skip cleansing than
          // show a Polish headline on an English article.
          let cleanTitle = item.clickbait && item.cleanTitle ? item.cleanTitle : (article?.title || '');
          let cleanDesc = item.clickbait && item.cleanDesc ? item.cleanDesc : (article?.description || '');
          if (article && item.clickbait && item.cleanTitle && isLanguageMismatch(article.title, item.cleanTitle)) {
            cleanTitle = article.title;
          }
          if (article && item.clickbait && item.cleanDesc && isLanguageMismatch(article.description, item.cleanDesc)) {
            cleanDesc = article.description;
          }

          results.set(item.id, {
            id: item.id,
            cleanTitle,
            cleanDesc,
            relevanceScore: typeof item.score === 'number' ? Math.min(10, Math.max(1, item.score)) : 5,
            category: item.clickbait && item.category ? item.category : undefined,
            explanation: item.clickbait && item.why ? item.why : undefined
          });
        }
      }
    }
  } catch {
    // Silent fail - return empty results
  }

  return results;
};

const analyzeWithAI = async (articles: Article[]): Promise<Article[]> => {
  const prompt = buildBatchPrompt(articles);
  const content = await callMistral(prompt);

  if (!content) {
    // Throw rather than silently returning heuristic results — the outer
    // wrapper relies on a thrown error to start `startBackgroundRetry`.
    // Returning heuristics here would look like a success and skip the
    // retry path, leaving the user with no AI analysis until they manually
    // refresh again (which the 30-min cooldown makes painful).
    throw new Error('Mistral call returned no content');
  }

  const batchResults = parseBatchResponse(content, articles);

  // Also parse the raw response to check clickbait flags
  let clickbaitFlags = new Map<number, boolean>();
  try {
    const cleanContent = content.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();
    const jsonMatch = cleanContent.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      if (Array.isArray(parsed)) {
        for (const item of parsed) {
          if (typeof item.id === 'number') {
            clickbaitFlags.set(item.id, item.clickbait === true);
          }
        }
      }
    }
  } catch {
    // Silent fail
  }

  return articles.map((article, index) => {
    const aiResult = batchResults.get(index);
    const isClickbait = clickbaitFlags.get(index) ?? false;

    return {
      ...article,
      isClickbait,
      cleanedTitle: isClickbait && aiResult ? aiResult.cleanTitle : article.title,
      cleanedDescription: isClickbait && aiResult ? aiResult.cleanDesc : article.description,
      clickbaitCategory: isClickbait && aiResult?.category ? aiResult.category : undefined,
      clickbaitExplanation: isClickbait && aiResult?.explanation ? aiResult.explanation : undefined,
      relevanceScore: aiResult?.relevanceScore ?? 5,
      aiAnalyzed: true,
    };
  });
};

// Helper to apply heuristic analysis to articles
export const applyHeuristicAnalysis = (articles: Article[]): Article[] => {
  return articles.map(article => {
    const analysis = analyzeClickbaitHeuristic(article.title);
    return {
      ...article,
      isClickbait: analysis.isClickbait,
      cleanedTitle: analysis.cleanedTitle,
      cleanedDescription: article.description,
      clickbaitCategory: analysis.category,
      clickbaitExplanation: analysis.explanation,
      relevanceScore: 5 // Default when AI unavailable
    };
  });
};

// Timeout wrapper that also returns whether it timed out
const withTimeoutAndFlag = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  fallback: T
): Promise<{ result: T; timedOut: boolean }> => {
  let timedOut = false;
  const timeoutPromise = new Promise<T>((resolve) => {
    setTimeout(() => {
      timedOut = true;
      resolve(fallback);
    }, timeoutMs);
  });

  const result = await Promise.race([promise, timeoutPromise]);
  return { result, timedOut };
};

// Track all active background retries so a single-feed reload doesn't
// silently kill another concurrent wave's retry. cancelBackgroundRetry
// clears every active timeout; nothing auto-clears on new retries.
const activeRetryTimeouts = new Set<ReturnType<typeof setTimeout>>();

export const cancelBackgroundRetry = (): void => {
  for (const id of activeRetryTimeouts) {
    clearTimeout(id);
  }
  activeRetryTimeouts.clear();
};

// Background retry with exponential backoff
const startBackgroundRetry = (
  articles: Article[],
  onSuccess: (analyzed: Article[]) => void,
  startTime: number = Date.now()
): void => {
  const MAX_RETRY_DURATION_MS = 15 * 60 * 1000; // 15 minutes
  const BASE_DELAY_MS = 30 * 1000; // Start with 30 seconds
  const MAX_DELAY_MS = 5 * 60 * 1000; // Cap at 5 minutes between retries

  const attemptRetry = (attempt: number): void => {
    const elapsed = Date.now() - startTime;
    if (elapsed >= MAX_RETRY_DURATION_MS) {
      return;
    }

    // Exponential backoff: 30s, 60s, 120s, 240s, 300s (capped)
    const delay = Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);

    const id = setTimeout(async () => {
      activeRetryTimeouts.delete(id);
      try {
        const analyzed = await analyzeWithAI(articles);
        const hasAIResults = analyzed.some(a => a.relevanceScore !== 5);
        if (hasAIResults) {
          onSuccess(analyzed);
        } else {
          attemptRetry(attempt + 1);
        }
      } catch {
        attemptRetry(attempt + 1);
      }
    }, delay);
    activeRetryTimeouts.add(id);
  };

  attemptRetry(0);
};

export interface AnalysisResult {
  articles: Article[];
  usedHeuristicFallback: boolean;
}

export const analyzeClickbaitWithAI = async (
  articles: Article[],
  aiAvailable: boolean,
  maxToAnalyze: number = 20,
  onBackgroundRetrySuccess?: (analyzed: Article[]) => void
): Promise<AnalysisResult> => {
  if (articles.length === 0) return { articles, usedHeuristicFallback: false };

  const toAnalyze = articles.slice(0, maxToAnalyze);
  const rest = articles.slice(maxToAnalyze);

  let analyzedBatch: Article[];
  let usedHeuristicFallback = false;

  if (aiAvailable) {
    const heuristicFallback = applyHeuristicAnalysis(toAnalyze);
    try {
      // Use timeout wrapper - if AI takes longer than 25s, fall back to heuristics
      const { result, timedOut } = await withTimeoutAndFlag(
        analyzeWithAI(toAnalyze),
        25000,
        heuristicFallback
      );
      analyzedBatch = result;
      usedHeuristicFallback = timedOut;

      // If timed out and callback provided, start background retry
      if (timedOut && onBackgroundRetrySuccess) {
        startBackgroundRetry(toAnalyze, (retryResult) => {
          // Combine retry results with the rest (which use heuristics)
          const analyzedRest = applyHeuristicAnalysis(rest);
          onBackgroundRetrySuccess([...retryResult, ...analyzedRest]);
        });
      }
    } catch {
      analyzedBatch = heuristicFallback;
      usedHeuristicFallback = true;

      // Start background retry on error too
      if (onBackgroundRetrySuccess) {
        startBackgroundRetry(toAnalyze, (retryResult) => {
          const analyzedRest = applyHeuristicAnalysis(rest);
          onBackgroundRetrySuccess([...retryResult, ...analyzedRest]);
        });
      }
    }
  } else {
    analyzedBatch = applyHeuristicAnalysis(toAnalyze);
  }

  // Apply heuristics only to the rest (beyond maxToAnalyze)
  const analyzedRest = applyHeuristicAnalysis(rest);

  return {
    articles: [...analyzedBatch, ...analyzedRest],
    usedHeuristicFallback
  };
};
